import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { fetchSettings } from "@/lib/api";
import { Mail, Phone, Clock } from "lucide-react";

const DEFAULT_EMAIL = "Brent@MovingMountainsAdvisors.com";
const DEFAULT_PHONE = "(863) 251-5433";
const DEFAULT_HOURS = "24 Hours";
const TEL_E164 = "+18632515433";

export default function Contact() {
    const [s, setS] = useState(null);
    useEffect(() => {
        fetchSettings().then(setS).catch(() => {});
    }, []);

    const email = s?.contact_email || DEFAULT_EMAIL;
    const phone = s?.contact_phone || DEFAULT_PHONE;
    const hours = s?.office_hours || DEFAULT_HOURS;
    // Tel link uses E.164 derived from the configured phone (digits only) — falls
    // back to the canonical +18632515433 when the configured value matches.
    const telHref = useMemo(() => {
        const digits = (phone || "").replace(/\D/g, "");
        if (!digits) return `tel:${TEL_E164}`;
        // 10-digit US -> prefix +1, otherwise prepend + and assume already-international.
        if (digits.length === 10) return `tel:+1${digits}`;
        if (digits.length === 11 && digits.startsWith("1")) return `tel:+${digits}`;
        return `tel:+${digits}`;
    }, [phone]);

    const jsonLd = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Moving Mountains Advisors",
        url: typeof window !== "undefined" ? window.location.origin : "https://movingmountainsinc.com",
        contactPoint: [{
            "@type": "ContactPoint",
            contactType: "customer service",
            email,
            telephone: TEL_E164,
            availableLanguage: ["English"],
            hoursAvailable: "Mo-Su 00:00-23:59",
        }],
    }), [email]);

    return (
        <div data-testid="contact-page">
            <SEO
                title="Contact — Moving Mountains Advisors"
                description="Reach Moving Mountains directly. Email, phone, and 24-hour availability — answered by a real person, no ticket queues."
                jsonLd={jsonLd}
            />
            <PageHeader
                eyebrow="Contact"
                title="Get in touch."
                subtitle="Prefer to reach out directly? Here's how. We respond personally — no ticket queues."
            />
            <section className="mm-section bg-white">
                <div className="mm-container grid gap-6 md:grid-cols-3">
                    <ContactCard
                        icon={Mail}
                        title="Email"
                        body={email}
                        href={`mailto:${email}`}
                    />
                    <ContactCard
                        icon={Phone}
                        title="Phone"
                        body={phone}
                        href={telHref}
                    />
                    <ContactCard
                        icon={Clock}
                        title="Hours of operation"
                        body={hours}
                    />
                </div>
            </section>
        </div>
    );
}

function ContactCard({ icon: Icon, title, body, href }) {
    const content = (
        <>
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-mm-navy to-mm-blue text-white">
                <Icon size={18} />
            </div>
            <div className="mm-eyebrow mb-1">{title}</div>
            <div className="mm-display text-xl text-mm-navy break-words">{body}</div>
        </>
    );
    return href ? (
        <a href={href} className="mm-card p-7 hover:shadow-hover transition-shadow" data-testid={`contact-${title.toLowerCase()}`}>
            {content}
        </a>
    ) : (
        <div className="mm-card p-7" data-testid={`contact-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            {content}
        </div>
    );
}
